import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Identifiable } from '@rtls/core/models/base/identifiable.model';
import { convoyFactory } from '@rtls/core/models/convoy/convoy.factory';
import { delaySummaryFactory } from '@rtls/core/models/convoy/delay-summary.factory';
import { contactFactory } from '@rtls/core/models/human/contact.factory';
import { companyFactory } from '@rtls/core/models/location/company.factory';
import { locationFactory } from '@rtls/core/models/location/location.factory';
import { Result } from '@rtls/core/models/result.model';

import { Pagination } from '@rtls/core/models/rpc/pagination.model';
import { SearchQuery } from '@rtls/core/models/rpc/search-query';

import { RtlsModels } from '@rtls/core/models/rtls/rtls-models';
import { RtlsService } from '@rtls/core/models/rtls/rtls-service.model';
import { typeFactory } from '@rtls/core/models/shared/type.factory';
import { businessFactory } from '@rtls/core/models/statistics/business.factory';
import { tagAttachmentFactory } from '@rtls/core/models/tag/tag-attachment.factory';
import { thingHistoryFactory } from '@rtls/core/models/thing/thing-history.factory';
import { thingFactory } from '@rtls/core/models/thing/thing.factory';
import { Factory } from 'factory.ts/lib';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';

@Injectable()
export class MockedRtlsService<T extends Identifiable> implements RtlsService<T> {
	private factory: Factory<any, string | number | symbol>;
	latency = 10;
	errorChancePercentage = 0;
	estimatedDataSetSize = 50;

	constructor() {}

	private generateRandom(id: number): T {
		const random = this.factory.build({});
		random.id = id;
		return random;
	}

	private generatePaginated(pagination?: Pagination): Observable<Result<T>> {
		const defensivePagination = pagination ? pagination : Pagination.default();
		defensivePagination.totalItems = this.estimatedDataSetSize;
		if (defensivePagination.pageSize === -1) {
			defensivePagination.pageSize = this.estimatedDataSetSize;
		}
		return of({
			pagination: defensivePagination,
			data: this.factory.buildList(defensivePagination.pageSize, {}),
		});
	}

	private throwErrorMaybe<X>(value: X): X {
		if (Math.random() * 100 < this.errorChancePercentage) {
			throw new Error();
		}
		return value;
	}

	// This is ugly but I cannot find a better way to do it without importing it in the prod build yet :(
	private getFactory(model: RtlsModels): Factory<any> {
		switch (model) {
			case RtlsModels.Business:
				return businessFactory;
			case RtlsModels.Company:
				return companyFactory;
			case RtlsModels.Driver:
				return contactFactory;
			case RtlsModels.Convoy:
				return convoyFactory;
			case RtlsModels.DelaySummary:
				return delaySummaryFactory;
			case RtlsModels.Location:
				return locationFactory;
			case RtlsModels.LocationType:
				return typeFactory;
			case RtlsModels.Step2:
				throw new Error(`No Factory created for ${model} yet ! :'(`);
			//  return storyFactory;
			case RtlsModels.TagAttachment:
				return tagAttachmentFactory;
			case RtlsModels.Thing:
				return thingFactory;
			case RtlsModels.ThingHistories:
				return thingHistoryFactory;
			case RtlsModels.ThingType:
				return typeFactory;
		}
		throw new Error(`No Factory created for ${model} yet ! :'(`);
	}

	useModel(models: RtlsModels): RtlsService<T> {
		const service = new MockedRtlsService<T>();
		Object.assign(service, this);
		service.factory = this.getFactory(models);
		return service;
	}

	useBaseUrl(baseUrl: string): void {
		const service = new MockedRtlsService<T>();
		Object.assign(service, this);
	}

	get(id: number): Observable<T> {
		return this.wrap(of(this.generateRandom(id)));
	}

	getByIDs(ids: number[]): Observable<Result<T>> {
		const result = {
			data: ids.map((id) => this.generateRandom(id)),
			pagination: Pagination.default(1, ids.length),
		};
		return this.wrap(of(result));
	}

	getAll(pagination?: Pagination): Observable<Result<T>> {
		return this.wrap(this.generatePaginated(pagination));
	}

	getTotalItems(): Observable<number | undefined> {
		return of(this.estimatedDataSetSize);
	}

	search(query: SearchQuery | null, pagination: Pagination, fuzzy: boolean = false): Observable<Result<T>> {
		return this.wrap(this.generatePaginated(pagination));
	}

	prebuiltSearch(searchTerms: string, pagination: Pagination): Observable<Result<T>> {
		return this.wrap(this.generatePaginated(pagination));
	}

	searchWithParams(
		query: SearchQuery | null,
		pagination: Pagination,
		httpParams?: HttpParams,
	): Observable<Result<T>> {
		return this.wrap(this.generatePaginated(pagination));
	}

	customDownload(uri: string): Observable<any> {
		return this.wrap(this.generatePaginated());
	}

	downloadResource(
		query: SearchQuery | null,
		pagination: Pagination,
		service: string,
		inBetweenEndpoint?: string,
		httpParams?: HttpParams,
	): Observable<Result<T>> {
		return this.wrap(this.generatePaginated(pagination));
	}

	// add general behaviour
	wrap<X>(observable: Observable<X>): Observable<X> {
		return observable.pipe(
			delay(this.latency),
			map((x: any) => this.throwErrorMaybe(x)),
		);
	}

	upsert(original: T, updated: T): Observable<T> {
		return this.wrap(of(updated));
	}

	update(original: T, updated: T): Observable<T> {
		return this.wrap(of(updated));
	}

	save(body: T): Observable<T> {
		return this.wrap(of(body));
	}

	delete(id: number, isSoft: boolean = false): Observable<T> {
		return this.wrap(of(this.generateRandom(id)));
	}

	deleteAll(isSoft: boolean = true): Observable<boolean> {
		return this.wrap(of(true));
	}

	saveBatch(body: T[]): Observable<T[]> {
		return this.wrap(of(body));
	}

	saveByPut(body: T, id?: number) {
		return this.wrap(of(body));
	}
}
